import { useState } from 'react';
import { Button, orderNoShort } from '@cuito/ui';
import type { PaymentMethod } from '@cuito/types';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';
import { VerifyCustomerCheckbox } from './VerifyCustomerCheckbox';

/**
 * 收款确认的二次确认弹窗（§6.4 逐字照抄 class）。
 *
 * 三件不能改的事：
 *   ① 金额**取服务端订单合计**（props 传入，组件不自己算）；
 *   ② 默认选中「微信」，选「线下」时追加 `现金收款请当面点清`（`text-xs text-gray-500`）；
 *   ③ 收款方式组是 `role="radiogroup"` + `role="radio" aria-checked`（§6.4 规则 6）。
 *
 * Phase 9 追加（§6.4 规则 5b、契约 §12.2）：**离线扫码取单**时弹窗里多一个核对勾选框
 * （`verify-customer-checkbox`），**未勾选 ⇒ 「确认收款」禁用**。这是 R-02（离线用错单收款）
 * 的缓解措施本体，而不是一句提示：禁用是**客户端的先拦**，服务端那一道是
 * 422 `CUSTOMER_VERIFICATION_REQUIRED`（`origin = "OFFLINE_SCAN"` 且缺勾选时）。
 * 两道都要有 —— 只靠服务端，店员会在断网时点出一个「失败」而不知道要勾什么。
 *
 * 勾选值**由本组件持有**（弹窗每次打开都重新挂载，`verified` 随挂载重置为 `false`）：
 * 「这一单核对过没有」是弹窗内的一次性动作，不是订单的状态。
 */
export const PAYMENT_OPTIONS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: 'WECHAT', label: '微信' },
  { value: 'ALIPAY', label: '支付宝' },
  { value: 'OFFLINE', label: '线下' },
];

/** 收款方式 → 界面词（§7.5；页面的「已确认收款（微信）」提示与它同源，不另写一张表） */
export function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_OPTIONS.find((option) => option.value === method)?.label ?? '';
}

/**
 * 未勾选时「确认收款」**为什么**是灰的（§6.4 规则 5b 的文案，逐字）。
 *
 * 与收银端别的禁用按钮同款纪律（`SCAN_NO_LOCATION_TEXT` 先例）：禁用必须**紧跟着理由**。
 * 只把按钮变灰、不说为什么，店员会反复点它并以为机器坏了 —— 那正是 R-02 想避免的现场。
 */
export const VERIFY_REQUIRED_TEXT = '请先勾选已与顾客核对订单';

/**
 * 离线时弹窗内的追加提示（§6.4 规则 5 的逐字文案）。
 *
 * 「确认后写入离线队列」这件事必须**在确认之前**说出来：确认了却没上传、而界面一声不吭，
 * 店员会以为钱已经记在系统里了（R-02 的另一半）。
 */
export const OFFLINE_UPLOAD_HINT = '当前离线，确认记录将在联网后上传';

interface Props {
  open: boolean;
  /** **服务端**的订单合计（元，两位小数字符串）；组件不做任何累加 */
  totalAmount: string;
  orderNo: string;
  busy: boolean;
  /**
   * Phase 9：离线扫码取单时才为 `true`（`origin = 'OFFLINE_SCAN'`）。
   *
   * 它同时决定三件事，三件都必须同源：① 勾选框出不出现；② 「确认收款」的禁用条件；
   * ③ 调用方提交时带不带 `origin` / `verifiedWithCustomer`（那一步在调用方，
   * 因为请求体的形状由收款那一条路拥有）。
   */
  offlineScan?: boolean;
  /**
   * 数据时间戳文案（`数据时间 HH:mm:ss`，见 `cashier/data/dataAsOf.ts`）；`null` ⇒ `数据时间 —`。
   *
   * Phase 9 追加（R-06 ④、§6.4 规则 7）：**必须**由调用方把「本机接收这份订单数据的时刻」
   * 算好之后传进来 —— 组件自己不取时间，否则「用的是接收时刻还是订单时间」就藏在组件里，
   * 而这两者的区别正是这条判据的全部内容。
   */
  dataAsOf?: string | null;
  /** 数据超过 5 分钟未刷新 ⇒ 时间戳转黄色警示（`text-amber-600`，§6.4 规则 7） */
  dataStale?: boolean;
  onCancel: () => void;
  /** `verified` 是核对勾选框的值；联网路径（`offlineScan !== true`）恒为 `false` 且被请求体忽略 */
  onConfirm: (method: PaymentMethod, verified: boolean) => void;
}

export default function PaymentConfirmModal({
  open,
  totalAmount,
  orderNo,
  busy,
  offlineScan = false,
  dataAsOf = null,
  dataStale = false,
  onCancel,
  onConfirm,
}: Props) {
  const [method, setMethod] = useState<PaymentMethod>('WECHAT');
  /** 核对勾选（§6.4 规则 5b）；弹窗重新打开 = 重新挂载 ⇒ 回到未勾选 */
  const [verified, setVerified] = useState(false);
  if (!open) return null;

  /** 未勾选 ⇒ 不许提交（M9-A 的变异点就在这一行；删掉它，用例必须红） */
  const verifyBlocked = offlineScan && !verified;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid="payment-modal"
        role="dialog"
        aria-modal="true"
        className="flex max-w-sm flex-col gap-4 rounded-2xl border-2 border-black bg-white p-6"
      >
        <div className="text-lg font-bold">确认已收到款项？</div>
        {/*
         * 金额行 + **右侧的数据时间戳**（§6.4 规则 7 冻结的形态：`data-testid="payment-data-asof"`，
         * 文案 `数据时间 HH:mm:ss`，`text-xs`；超过 5 分钟转 `text-amber-600`）。
         * 金额那一段的文案与分隔符逐字不变（`金额 ¥120.00 · 订单 C8K3M2XB · 确认后顾客端会显示「已收款」。`），
         * 只是把它挪进一个 `justify-between` 的 flex 里，好让时间戳落在**右侧**。
         */}
        <div className="flex items-start justify-between gap-2 text-sm text-gray-500">
          <span>
            金额 {formatServerYuan(totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT} · 订单{' '}
            {orderNoShort(orderNo)} · 确认后顾客端会显示「已收款」。
          </span>
          <span
            data-testid="payment-data-asof"
            className={dataStale ? 'shrink-0 text-xs text-amber-600' : 'shrink-0 text-xs text-gray-500'}
          >
            {dataAsOf ?? '数据时间 —'}
          </span>
        </div>

        <div className="flex gap-2" role="radiogroup" aria-label="收款方式">
          {PAYMENT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={method === option.value}
              onClick={() => setMethod(option.value)}
              className={`flex-1 rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
                method === option.value ? 'bg-black text-white' : 'bg-white text-black'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {method === 'OFFLINE' && <p className="text-xs text-gray-500">现金收款请当面点清</p>}

        {/*
         * 核对勾选（§6.4 规则 5b）**只在离线扫码取单时出现**：联网时服务端是权威，
         * 订单码不是唯一凭据 ⇒ 联网路径上多出这个勾选框就是无出处的新 UI。
         * 未勾选时紧跟一句理由（与禁用同源：`verifyBlocked`），不让店员对着灰按钮猜。
         */}
        {offlineScan && <p className="text-xs text-gray-500">{OFFLINE_UPLOAD_HINT}</p>}
        {offlineScan && <VerifyCustomerCheckbox checked={verified} onChange={setVerified} />}
        {verifyBlocked && <p className="text-xs text-gray-500">{VERIFY_REQUIRED_TEXT}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            取消
          </Button>
          <Button
            size="lg"
            onClick={() => onConfirm(method, verified)}
            disabled={busy || verifyBlocked}
            className="disabled:bg-gray-200 disabled:text-gray-400"
          >
            {busy ? '确认中…' : '确认收款'}
          </Button>
        </div>
      </div>
    </div>
  );
}
